import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Dropdown from "../../components/common/Dropdown";
import TextInput from "../../components/ui/TextInput";
import {
    activateAwaitingUserAdmin,
    deactivateActiveUserAdmin,
    deleteAwaitingUserAdmin,
    getActiveUsersAdmin,
    getAwaitingUsersAdmin,
} from "../../api/administration";
import { User } from "../../types/types";
import { getCompanies, registerAdmin } from "../../api";
import { validatePassword } from "../../utils/passwordValidation";
import Icon from "../../components/common/Icon";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";

interface Company {
    label: string;
    value: string;
}

const Users: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");
    const [awaitingUsers, setAwaitingUsers] = useState<User[]>([]);
    const [activeUsers, setActiveUsers] = useState<User[]>([]);
    const [company, setCompany] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [companies, setCompanies] = useState<Company[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [registrationSuccess, setRegistrationSuccess] = useState<boolean>(false);

    const passwordValidation = useMemo(() => validatePassword(password), [password]);
    const showPasswordRequirements = password.length > 0;
    const isFormComplete = company.trim() !== "" && email.trim() !== "" && passwordValidation.isValid;

    useEffect(() => {
        fetchUsers();
        getCompaniesAPI();
    }, []);

    const getCompaniesAPI = async () => {
        try {
            const response = await getCompanies();
            if (response) {
                const companiesList: Company[] = response.map((company: { name: string; id: string }) => ({
                    label: company.name,
                    value: company.id,
                }));
                setCompanies(companiesList);
            }
        } catch (error) {
            console.error("Error getting companies:", error);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const [awaiting, active] = await Promise.all([getAwaitingUsersAdmin(token), getActiveUsersAdmin(token)]);
            setAwaitingUsers(awaiting);
            setActiveUsers(active);
        } catch (error) {
            console.error("Failed to fetch users", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async () => {
        try {
            await registerAdmin({ email, password, company });
            await fetchUsers();
            setRegistrationSuccess(true);
            setEmail("");
            setPassword("");
            setCompany("");
            setErrorMessage("");
        } catch (error) {
            console.error("Error registering:", error);
            setErrorMessage(t("registrationFailed"));
        }
    };

    const handleActivateUser = async (userId: string) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            await activateAwaitingUserAdmin({ token, userIdToActivate: userId });
            await fetchUsers();
        } catch (error) {
            console.error("Failed to activate user", error);
        }
    };

    const handleDeactivateUser = async (userId: string) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            await deactivateActiveUserAdmin({ token, userIdToDeactivate: userId });
            await fetchUsers();
        } catch (error) {
            console.error("Failed to deactivate user", error);
        }
    };

    const handleDeleteUser = async (user: User) => {
        const confirmed = window.confirm(
            t("confirmDeleteAwaitingUser", {
                email: user.email,
                defaultValue: `Delete awaiting user ${user.email}? This cannot be undone.`,
            }),
        );
        if (!confirmed) return;

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            await deleteAwaitingUserAdmin({ token, userIdToDelete: user.id });
            await fetchUsers();
        } catch (error) {
            console.error("Failed to delete user", error);
        }
    };

    if (loading) return <div>Chargement...</div>;

    const filterBySearch = (list: User[]) =>
        list
            .filter((user) => user.email.toLowerCase().includes(search.toLowerCase()))
            .sort((a, b) => (a.email && b.email ? a.email.localeCompare(b.email) : 0));

    const filteredAwaitingUsers = filterBySearch(awaitingUsers);
    const filteredActiveUsers = filterBySearch(activeUsers);

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("users")} subtitle={t("usersSubTitle")} />
            </div>

            <section className={adminSurface.sectionFull}>
                <h2 className={adminSurface.sectionTitle}>{t("addNewUser")}</h2>
                <p className={adminSurface.sectionHelp}>
                    {t("addNewUserHelp") || "L'utilisateur recevra ses identifiants par e-mail."}
                </p>

                <div className={`${adminSurface.addForm} ${adminSurface.card}`}>
                    <div className={`${adminSurface.addRow} ${adminSurface.addRowNowrap}`}>
                        <div className={`${adminSurface.addField} ${adminSurface.addFieldW240}`}>
                            <label className={adminSurface.addLabel}>
                                {t("companyName")}
                                <span className="required-asterisk">*</span>
                            </label>
                            <Dropdown
                                label=""
                                options={companies}
                                onChange={(value) => setCompany(value)}
                                placeholder={t("companyName")}
                                loading={false}
                                error={Boolean(errorMessage)}
                                value={company}
                                isWithoutMargin
                                fullWidth
                            />
                        </div>

                        <div className={`${adminSurface.addField} ${adminSurface.addFieldW260}`}>
                            <label className={adminSurface.addLabel} htmlFor="newUserEmail">
                                {t("email")}
                                <span className="required-asterisk">*</span>
                            </label>
                            <TextInput
                                type="email"
                                placeholder={t("enterYourEmailMandatory")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className={`${adminSurface.addField} ${adminSurface.addFieldW200}`}>
                            <label className={adminSurface.addLabel} htmlFor="newUserPassword">
                                {t("password") || "Mot de passe"}
                                <span className="required-asterisk">*</span>
                            </label>
                            <TextInput
                                type="password"
                                placeholder={t("enterYourPasswordMandatory")}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        <button
                            className={adminSurface.addButton}
                            type="button"
                            onClick={handleAddUser}
                            disabled={!isFormComplete}
                        >
                            <Icon name="ic-plus" className="ic" size={16} />
                            {t("createAccount")}
                        </button>
                    </div>

                    {(showPasswordRequirements || errorMessage || registrationSuccess) && (
                        <div style={{ padding: "0 20px 16px" }}>
                            {showPasswordRequirements && (
                                <div
                                    className="password-requirements"
                                    style={{ fontSize: "12px", marginTop: "4px", marginBottom: "8px" }}
                                >
                                    <p style={{ margin: "0 0 4px 0", fontWeight: 500 }}>{t("passwordRequirements")}:</p>
                                    <ul style={{ margin: 0, paddingLeft: "20px", listStyle: "none" }}>
                                        <li
                                            style={{
                                                color: passwordValidation.requirements.minLength ? "green" : "red",
                                            }}
                                        >
                                            {passwordValidation.requirements.minLength ? "✓" : "✗"}{" "}
                                            {t("passwordMinLength")}
                                        </li>
                                        <li
                                            style={{
                                                color: passwordValidation.requirements.hasUppercase ? "green" : "red",
                                            }}
                                        >
                                            {passwordValidation.requirements.hasUppercase ? "✓" : "✗"}{" "}
                                            {t("passwordUppercase")}
                                        </li>
                                        <li
                                            style={{
                                                color: passwordValidation.requirements.hasLowercase ? "green" : "red",
                                            }}
                                        >
                                            {passwordValidation.requirements.hasLowercase ? "✓" : "✗"}{" "}
                                            {t("passwordLowercase")}
                                        </li>
                                        <li
                                            style={{
                                                color: passwordValidation.requirements.hasNumber ? "green" : "red",
                                            }}
                                        >
                                            {passwordValidation.requirements.hasNumber ? "✓" : "✗"}{" "}
                                            {t("passwordNumber")}
                                        </li>
                                        <li
                                            style={{
                                                color: passwordValidation.requirements.hasSpecial ? "green" : "red",
                                            }}
                                        >
                                            {passwordValidation.requirements.hasSpecial ? "✓" : "✗"}{" "}
                                            {t("passwordSpecial")}
                                        </li>
                                    </ul>
                                </div>
                            )}

                            {errorMessage && (
                                <p className="error-message" style={{ color: "red", margin: 0 }}>
                                    {errorMessage}
                                </p>
                            )}

                            {registrationSuccess && !errorMessage && (
                                <p style={{ color: "var(--green-800)", margin: 0, fontSize: "13.5px" }}>
                                    {t("registrationSuccess") || "User created and awaiting activation."}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchUser")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <h2 className={adminSurface.sectionTitle}>{t("awaitingUsersTitle")}</h2>
            <p className={adminSurface.sectionHelp}>{t("awaitingUsersHelp")}</p>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("email")}</th>
                                <th>{t("status") || "Status"}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAwaitingUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "center", color: "var(--ink-3)" }}>
                                        {t("noAwaitingUsers")}
                                    </td>
                                </tr>
                            ) : (
                                filteredAwaitingUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td style={{ fontWeight: 600 }}>{user.email}</td>
                                        <td>
                                            <span
                                                className={`${adminSurface.statusBadge} ${adminSurface.statusPending}`}
                                            >
                                                {t("awaiting")}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="cities-table-actions" style={{ display: "flex", gap: 8 }}>
                                                <button
                                                    className={adminSurface.modifyButton}
                                                    onClick={() => handleActivateUser(user.id)}
                                                >
                                                    {t("activate")}
                                                </button>
                                                <button
                                                    className={adminSurface.deleteButton}
                                                    onClick={() => handleDeleteUser(user)}
                                                >
                                                    {t("delete")}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredAwaitingUsers.length} {t("awaitingUsers")}
                    </span>
                </div>
            </div>

            <h2 className={adminSurface.sectionTitle} style={{ marginTop: 28 }}>
                {t("activeUsersTitle")}
            </h2>
            <p className={adminSurface.sectionHelp}>{t("activeUsersHelp")}</p>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("email")}</th>
                                <th>{t("status") || "Status"}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredActiveUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "center", color: "var(--ink-3)" }}>
                                        {t("noActiveUsers")}
                                    </td>
                                </tr>
                            ) : (
                                filteredActiveUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td style={{ fontWeight: 600 }}>{user.email}</td>
                                        <td>
                                            <span className={adminSurface.statusBadge}>{t("active")}</span>
                                        </td>
                                        <td>
                                            {user.isOwner ? (
                                                <span style={{ color: "var(--ink-3)", fontSize: "13.5px" }}>
                                                    {t("owner")}
                                                </span>
                                            ) : (
                                                <button
                                                    className={adminSurface.deleteButton}
                                                    onClick={() => handleDeactivateUser(user.id)}
                                                >
                                                    {t("deactivate")}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredActiveUsers.length} {t("activeUsers")}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default Users;
